// Şub 27, 2026 — DPD Dosya Kategorileri (v2)
// - Kategori bazlı toplu içe aktar (üstteki genel + her kategori başlığında)
// - Junk dosyalar (image.png, signature vb.) ayrı kategori + sil butonu
// - Dosya tıklama ile önizleme/indirme
// - Aynı dosya tekrar geldiğinde otomatik skip
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Input } from '../../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  FileText, DownloadCloud, CheckCircle2, Circle, RefreshCw, Loader2,
  FolderTree, Trash2, ExternalLink, PlayCircle, RotateCcw, FileArchive,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../lib/auth';
import DPDAutoIngestSettings from './DPDAutoIngestSettings';

const TYPE_COLORS = {
  route_datalog: 'bg-blue-50 text-blue-700 border-blue-200',
  retour_report: 'bg-red-50 text-red-700 border-red-200',
  stops_parcels: 'bg-green-50 text-green-700 border-green-200',
  dispatcher_debrief: 'bg-purple-50 text-purple-700 border-purple-200',
  facturatie: 'bg-amber-50 text-amber-700 border-amber-200',
  unknown: 'bg-gray-50 text-gray-700 border-gray-200',
  junk: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function DPDFileCategories() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [excludePattern, setExcludePattern] = useState(() => localStorage.getItem('dpd_fc_exclude') || '');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('dpd_fc_sort') || 'mail_desc'); // mail_desc | mail_asc | arc_desc | arc_asc
  const [data, setData] = useState({ groups: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [ingestingId, setIngestingId] = useState(null);
  const [bulkIngestingType, setBulkIngestingType] = useState(null); // null | 'all' | type
  const [resettingType, setResettingType] = useState(null); // null | 'all' | type
  const [zipping, setZipping] = useState(false);

  useEffect(() => { localStorage.setItem('dpd_fc_exclude', excludePattern); }, [excludePattern]);
  useEffect(() => { localStorage.setItem('dpd_fc_sort', sortOrder); }, [sortOrder]);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    });
  }, []);

  const fetchGroups = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ firm_id: firmId });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await api.get(`/dpd/file-categories?${params.toString()}`);
      setData(r.data?.data || { groups: [], total: 0 });
    } catch (e) {
      toast.error('Kategoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, dateFrom, dateTo]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const downloadZip = async () => {
    if (!firmId || !dateFrom || !dateTo) {
      toast.error('Tarih aralığı seçmelisin');
      return;
    }
    setZipping(true);
    try {
      const params = new URLSearchParams({
        firm_id: firmId, date_from: dateFrom, date_to: dateTo,
      });
      const r = await api.get(`/dpd/download-archive-zip?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPD-Arsiv-${dateFrom}_${dateTo}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const included = r.headers?.['x-files-included'] || '?';
      toast.success(`ZIP indirildi (${included} dosya)`);
    } catch (e) {
      const msg = e.response?.status === 404
        ? 'Bu tarih aralığında dosya bulunamadı'
        : (e.response?.data?.detail || 'ZIP indirilemedi');
      toast.error(msg);
    } finally {
      setZipping(false);
    }
  };

  const ingestOne = async (mailArchiveId) => {
    setIngestingId(mailArchiveId);
    try {
      const r = await api.post(`/dpd/ingest-one?firm_id=${firmId}&mail_archive_id=${mailArchiveId}`);
      if (r.data?.data?.skipped) toast.info(t('dpd.fileCategories.toast.alreadyIngested'));
      else toast.success(t('dpd.fileCategories.toast.importedRows', { n: r.data?.data?.rows || 0 }));
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.toast.importFailed'));
    } finally {
      setIngestingId(null);
    }
  };

  const ingestBulk = async (reportType = null) => {
    setBulkIngestingType(reportType || 'all');
    try {
      const params = new URLSearchParams({ firm_id: firmId });
      if (reportType) params.set('report_type', reportType);
      if (excludePattern.trim()) params.set('exclude_pattern', excludePattern.trim());
      const r = await api.post(`/dpd/ingest-from-archive?${params.toString()}`);
      const d = r.data?.data;
      const parts = [t('dpd.fileCategories.toast.bulkResult', { ingested: d.ingested })];
      if (d.skipped_already) parts.push(t('dpd.fileCategories.toast.bulkAlready', { n: d.skipped_already }));
      if (d.skipped_junk) parts.push(t('dpd.fileCategories.toast.bulkJunk', { n: d.skipped_junk }));
      if (d.skipped_unknown) parts.push(t('dpd.fileCategories.toast.bulkUnknown', { n: d.skipped_unknown }));
      // Şub 27, 2026 — DPD sabah 10:00 dosyaları (eski versiyonlar) atlandı
      if (d.skipped_earlier) parts.push(`${d.skipped_earlier} eski sürüm atlandı (sabah versiyonu)`);
      if (d.errors?.length) parts.push(t('dpd.fileCategories.toast.bulkErrors', { n: d.errors.length }));
      toast.success(parts.join(', '));
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.toast.bulkFailed'));
    } finally {
      setBulkIngestingType(null);
    }
  };

  const previewFile = async (mailArchiveId) => {
    try {
      const r = await api.get(`/dpd/file-download-url/${mailArchiveId}?firm_id=${firmId}`);
      const url = r.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.toast.previewFailed'));
    }
  };

  const deleteFile = async (mailArchiveId) => {
    try {
      await api.delete(`/dpd/mail-entry/${mailArchiveId}?firm_id=${firmId}`);
      toast.success(t('dpd.fileCategories.toast.deleted'));
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.toast.deleteFailed'));
    }
  };

  const deleteJunkAll = async (group) => {
    let done = 0, fail = 0;
    for (const f of group.files) {
      try {
        await api.delete(`/dpd/mail-entry/${f.id}?firm_id=${firmId}`);
        done += 1;
      } catch { fail += 1; }
    }
    toast.success(t('dpd.fileCategories.toast.junkDeleted', { done }) + (fail ? `, ${fail} !` : ''));
    fetchGroups();
  };

  const resetAll = async () => {
    setResettingType('all');
    try {
      const r = await api.post(`/dpd/reset-all?firm_id=${firmId}`);
      const total = Object.values(r.data?.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(t('dpd.fileCategories.resetDone', { n: total }));
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.resetFailed'));
    } finally {
      setResettingType(null);
    }
  };

  const resetCategory = async (reportType) => {
    setResettingType(reportType);
    try {
      const r = await api.post(`/dpd/reset-category?firm_id=${firmId}&report_type=${reportType}`);
      const d = r.data?.data || {};
      const total = (d.deleted_rows || 0) + (d.deleted_ingest_log || 0);
      toast.success(t('dpd.fileCategories.resetDone', { n: total }));
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('dpd.fileCategories.resetFailed'));
    } finally {
      setResettingType(null);
    }
  };

  const filteredData = useMemo(() => {
    const patterns = excludePattern
      .split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const sortKey = sortOrder.startsWith('mail_') ? 'mail_date' : 'archived_at';
    const asc = sortOrder.endsWith('_asc');
    const groups = (data.groups || []).map((g) => {
      let files = g.files || [];
      if (patterns.length) {
        files = files.filter((f) => {
          const name = (f.filename || '').toLowerCase();
          return !patterns.some((p) => name.includes(p));
        });
      }
      files = [...files].sort((a, b) => {
        const av = String(a[sortKey] || '');
        const bv = String(b[sortKey] || '');
        return asc ? av.localeCompare(bv) : bv.localeCompare(av);
      });
      const ingestedInFiltered = files.filter((f) => f.ingested).length;
      return { ...g, files, count: files.length, ingested_count: ingestedInFiltered };
    });
    const total = groups.reduce((s, g) => s + g.count, 0);
    return { groups, total };
  }, [data, excludePattern, sortOrder]);

  return (
    <div className="space-y-4" data-testid="dpd-file-categories-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5 text-indigo-600" />{t('dpd.fileCategories.title')}
            </CardTitle>
            <CardDescription>
              {t('dpd.fileCategories.subtitle')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Şub 27, 2026 — SUPER_ADMIN için otomatik cron ayarları */}
            {isSuperAdmin && <DPDAutoIngestSettings />}
            {isSuperAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-red-700 border-red-300 hover:bg-red-50"
                    disabled={resettingType || bulkIngestingType || loading || !firmId}
                    data-testid="dpd-reset-all-btn"
                  >
                    {resettingType === 'all'
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <RotateCcw className="h-4 w-4 mr-2" />}
                    {t('dpd.fileCategories.resetAll')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('dpd.fileCategories.resetAllTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('dpd.fileCategories.resetAllBody')}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('dpd.common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={resetAll} className="bg-red-600 hover:bg-red-700" data-testid="dpd-reset-all-confirm">
                      {t('dpd.fileCategories.resetAll')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button onClick={() => ingestBulk(null)} disabled={bulkIngestingType || loading} data-testid="dpd-ingest-all-btn">
              {bulkIngestingType === 'all' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-2" />}
              {t('dpd.fileCategories.ingestAll')}
            </Button>
            <Button
              variant="outline"
              onClick={downloadZip}
              disabled={zipping || !firmId || !dateFrom || !dateTo}
              title={!dateFrom || !dateTo ? 'Önce tarih aralığı seç' : `${dateFrom} → ${dateTo} arası ekleri ZIP indir`}
              data-testid="dpd-download-zip-btn"
            >
              {zipping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileArchive className="h-4 w-4 mr-2" />}
              Ham Excelleri ZIP İndir
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="dpd-fc-firm"><SelectValue placeholder={t('dpd.common.firm')} /></SelectTrigger>
              <SelectContent>{firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder={t('dpd.common.dateFrom')} data-testid="dpd-fc-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder={t('dpd.common.dateTo')} data-testid="dpd-fc-date-to" />
            <Input
              type="text"
              value={excludePattern}
              onChange={(e) => setExcludePattern(e.target.value)}
              placeholder={t('dpd.fileCategories.excludePlaceholder')}
              data-testid="dpd-fc-exclude"
            />
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger data-testid="dpd-fc-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mail_desc">{t('dpd.fileCategories.sort.mailDesc')}</SelectItem>
                <SelectItem value="mail_asc">{t('dpd.fileCategories.sort.mailAsc')}</SelectItem>
                <SelectItem value="arc_desc">{t('dpd.fileCategories.sort.arcDesc')}</SelectItem>
                <SelectItem value="arc_asc">{t('dpd.fileCategories.sort.arcAsc')}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchGroups} variant="outline" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
          </div>

          <div className="text-sm text-gray-500 mb-3">
            {t('dpd.fileCategories.totalSummary', { files: filteredData.total, cats: filteredData.groups.length })}
            {excludePattern.trim() && (
              <span className="ml-2 text-orange-600">
                · {t('dpd.fileCategories.filteredHint', { n: (data.total || 0) - filteredData.total })}
              </span>
            )}
          </div>

          <Accordion type="multiple" className="space-y-2" defaultValue={filteredData.groups.map((g) => g.type)}>
            {filteredData.groups.map((g) => (
              <AccordionItem key={g.type} value={g.type} className="border rounded-lg">
                <div className="flex items-center gap-2 px-4">
                  <AccordionTrigger className="flex-1 hover:no-underline py-3">
                    <div className="flex items-center gap-3 w-full">
                      <Badge variant="outline" className={TYPE_COLORS[g.type] || TYPE_COLORS.unknown}>
                        {g.label}
                      </Badge>
                      <span className="text-sm font-medium">{t('dpd.fileCategories.countFiles', { n: g.count })}</span>
                      {!g.is_junk && (
                        <span className="text-xs text-gray-500">{t('dpd.fileCategories.countIngested', { n: g.ingested_count })}</span>
                      )}
                    </div>
                  </AccordionTrigger>
                  {/* Category-level actions */}
                  {!g.is_junk && g.type !== 'unknown' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => ingestBulk(g.type)}
                      disabled={bulkIngestingType || loading || g.count === g.ingested_count}
                      data-testid={`dpd-ingest-cat-${g.type}`}
                    >
                      {bulkIngestingType === g.type
                        ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        : <PlayCircle className="h-3 w-3 mr-1" />}
                      {t('dpd.fileCategories.ingestCat')}
                    </Button>
                  )}
                  {isSuperAdmin && !g.is_junk && g.type !== 'unknown' && g.ingested_count > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-700 border-red-300 hover:bg-red-50"
                          disabled={resettingType || loading}
                          data-testid={`dpd-reset-cat-${g.type}`}
                        >
                          {resettingType === g.type
                            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            : <RotateCcw className="h-3 w-3 mr-1" />}
                          {t('dpd.fileCategories.resetCat')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('dpd.fileCategories.resetCatTitle', { cat: g.label })}</AlertDialogTitle>
                          <AlertDialogDescription>{t('dpd.fileCategories.resetCatBody')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('dpd.common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => resetCategory(g.type)} className="bg-red-600 hover:bg-red-700" data-testid={`dpd-reset-cat-confirm-${g.type}`}>
                            {t('dpd.fileCategories.resetCat')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {g.is_junk && g.count > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-700" data-testid="dpd-junk-delete-all">
                          <Trash2 className="h-3 w-3 mr-1" />{t('dpd.fileCategories.deleteAll')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('dpd.fileCategories.dialog.deleteJunkTitle', { n: g.count })}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('dpd.fileCategories.dialog.deleteJunkBody')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('dpd.common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteJunkAll(g)} className="bg-red-600 hover:bg-red-700">
                            {t('dpd.common.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
                <AccordionContent className="px-2 pb-2">
                  <div className="rounded-md border overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[38%]"><FileText className="inline h-3 w-3 mr-1" />{t('dpd.fileCategories.cols.file')}</TableHead>
                          <TableHead className="w-[110px]">{t('dpd.fileCategories.cols.mailDate')}</TableHead>
                          <TableHead className="w-[110px]">{t('dpd.fileCategories.cols.archived')}</TableHead>
                          <TableHead>{t('dpd.fileCategories.cols.from')}</TableHead>
                          <TableHead className="w-[120px]">{t('dpd.fileCategories.cols.status')}</TableHead>
                          <TableHead className="w-[150px] text-right">{t('dpd.fileCategories.cols.action')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {g.files.map((f) => (
                          <TableRow key={f.id} data-testid={`dpd-file-${f.id}`}>
                            <TableCell className="max-w-[300px]">
                              <button
                                type="button"
                                onClick={() => previewFile(f.id)}
                                className="text-left hover:text-indigo-600 truncate flex items-center gap-1 w-full"
                                title={`Önizle: ${f.filename}`}
                              >
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{f.filename}</span>
                              </button>
                              {/* Şub 27, 2026 — DPD sürüm etiketleri (aynı gün için birden çok gönderim) */}
                              {f.is_earlier_version && (
                                <div className="mt-0.5 flex items-center gap-1">
                                  <Badge variant="outline" className="text-[9px] py-0 px-1 border-orange-300 text-orange-700 bg-orange-50 dark:bg-orange-900/20">
                                    Eski sürüm — {f.file_timestamp?.slice(11, 16) || ''} — atlanır
                                  </Badge>
                                </div>
                              )}
                              {f.is_latest && !f.is_earlier_version && f.file_timestamp && (
                                <div className="mt-0.5 flex items-center gap-1">
                                  <Badge variant="outline" className="text-[9px] py-0 px-1 border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20">
                                    Nihai — {f.file_timestamp.slice(11, 16)}
                                  </Badge>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-gray-700 font-medium" data-testid={`dpd-file-maildate-${f.id}`}>
                              {f.mail_date ? String(f.mail_date).slice(0, 10) : '-'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-gray-500 text-xs">
                              {f.archived_at ? f.archived_at.slice(0, 10) : '-'}
                            </TableCell>
                            <TableCell className="truncate max-w-[200px] text-gray-600" title={f.from_addr}>
                              {f.from_addr || '-'}
                            </TableCell>
                            <TableCell>
                              {f.is_junk ? (
                                <Badge variant="outline" className="gap-1 text-orange-700 border-orange-300">
                                  {t('dpd.fileCategories.status.junk')}
                                </Badge>
                              ) : f.ingested && f.ingest_active === false ? (
                                <Badge variant="outline" className="gap-1 text-gray-500 border-gray-300" title={f.superseded_by_filename || ''}>
                                  <Circle className="h-3 w-3" />
                                  Üzerine yazıldı ({f.ingested_rows ?? 0} satır)
                                </Badge>
                              ) : f.ingested ? (
                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {t('dpd.fileCategories.status.rows', { n: f.ingested_rows ?? 0 })}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1 text-gray-500">
                                  <Circle className="h-3 w-3" />{t('dpd.fileCategories.status.waiting')}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {!f.is_junk && g.type !== 'unknown' && (
                                  <Button
                                    size="sm"
                                    variant={f.ingested ? "ghost" : "default"}
                                    onClick={() => ingestOne(f.id)}
                                    disabled={ingestingId === f.id || f.ingested}
                                    data-testid={`dpd-ingest-${f.id}`}
                                    className="h-7 px-2"
                                    title="İçe aktar"
                                  >
                                    {ingestingId === f.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : f.ingested ? (
                                      <CheckCircle2 className="h-3 w-3" />
                                    ) : (
                                      <DownloadCloud className="h-3 w-3" />
                                    )}
                                  </Button>
                                )}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-red-600 hover:text-red-700"
                                      data-testid={`dpd-delete-${f.id}`}
                                      title="Sil"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('dpd.fileCategories.dialog.deleteFileTitle')}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('dpd.fileCategories.dialog.deleteFileBody', { filename: f.filename })}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('dpd.common.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteFile(f.id)} className="bg-red-600 hover:bg-red-700">
                                        {t('dpd.common.delete')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
            {!loading && filteredData.groups.length === 0 && (
              <div className="text-center text-gray-500 py-10">
                {t('dpd.fileCategories.emptyState')}<br />
                <span className="text-xs">{t('dpd.fileCategories.emptyHint')}</span>
              </div>
            )}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
